/**
 * The tournament's clock — the one module that knows what time it is at the tournament.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS EXISTS (RUN 83)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The hockey is played in Japan, and every date the Technical Delegate works with is a date in
 * the host city: a fixture's `date` is a calendar day there, a hearing's `hearingAt` is read
 * back in Asia/Tokyo on the case file, and the 24-hour appeal clock is counted from an incident
 * that happened there. The DEVICE the page is open on is whatever the TD's laptop or the UM's
 * phone happens to be set to, and during a tournament that is very often not Tokyo — a TD who
 * flew in yesterday, a UM whose phone never left European time, anyone opening the build from
 * home.
 *
 * RUN 82G fixed the Command Desk after its second independent QA found `todayIso` and the
 * kick-off countdown reading the device while every played rule on the same page read Tokyo.
 * That fix was right and it was local: a grep on 18 September 2026 found nine more surfaces
 * still reading the device, and each would otherwise have grown its own copy of the same six
 * lines. This module is that copy, made once. `desk/derive.ts` now asks it rather than keeping
 * its own, and so does everything the RUN 83 sweep found.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * AN INSTANT IS RE-ZONED. A CALENDAR DAY IS NOT.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The distinction this module exists to keep straight — and the one a fix here does harm by
 * blurring:
 *
 *  * **An instant** — `2026-09-24T05:00:00.000Z` — is a moment. To print it, or to cut a day
 *    and a time out of it, you must say on whose clock. That is what everything below does,
 *    and the clock is always the tournament's.
 *  * **A calendar day** — `2026-09-24` — is a NAME, not a moment. It is already the host
 *    city's day. `dayLabel`, `dayMonth` and `tokyoDateKicker` parse those in UTC and read them
 *    back in UTC deliberately, and nothing here should be pointed at them: re-zoning a name
 *    moves it, which would be the bug rather than the fix.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NOTHING HERE READS THE CLOCK ON ITS OWN
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Every function takes the instant as an argument. The caller defaults it to `new Date()` where
 * that is what it means, exactly as `todayIso` already did — that is the pattern this codebase
 * uses to keep time testable, and it is followed rather than replaced.
 */

/**
 * The tournament's own zone. **The single definition**: `appointments/vocabulary.ts` and
 * `hearings/documentText.ts` each used to declare their own copy of this string, and both now
 * re-export this one, so no existing import had to change and two constants cannot drift apart.
 */
export const TOURNAMENT_TIMEZONE = "Asia/Tokyo";

/**
 * One reading of the tournament's clock, as numbers. `month` is 1–12, as a calendar prints it,
 * not 0–11 as `Date` counts it.
 */
export interface TournamentClockParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

/**
 * The formatters, built once. `Intl.DateTimeFormat` construction is the expensive part and
 * these are hot — `quickNoteWhen` runs per note, `receiptStamp` per receipt — so each shape is
 * constructed at module load rather than per call.
 *
 * `en-GB` with numeric parts only, and `hourCycle: "h23"` so midnight is `00`, not `24`: what
 * comes back is digits, and no locale's spelling or layout can reach the screen through them.
 */
const CLOCK = new Intl.DateTimeFormat("en-GB", {
  timeZone: TOURNAMENT_TIMEZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

const DATE_SHORT = new Intl.DateTimeFormat("en-GB", {
  timeZone: TOURNAMENT_TIMEZONE,
  day: "2-digit",
  month: "short",
});

const DATE_LONG = new Intl.DateTimeFormat("en-GB", {
  timeZone: TOURNAMENT_TIMEZONE,
  day: "2-digit",
  month: "short",
  year: "numeric",
});

/** `value` padded to two digits — every shape below is fixed-width. */
function pad(value: number): string {
  return String(value).padStart(2, "0");
}

/**
 * What a wall clock in the host city reads at `at`.
 *
 * The parts are read individually rather than a formatted string being taken apart, because a
 * locale's LAYOUT is not a contract: `en-GB` puts the day first today and a runtime with
 * different ICU data need not. `formatToParts` names each number, so nothing here depends on
 * the order they come back in.
 */
export function tournamentClockParts(at: Date): TournamentClockParts {
  const parts = CLOCK.formatToParts(at);
  const read = (type: Intl.DateTimeFormatPartTypes): number =>
    Number.parseInt(parts.find((part) => part.type === type)?.value ?? "0", 10);
  return {
    year: read("year"),
    month: read("month"),
    day: read("day"),
    hour: read("hour"),
    minute: read("minute"),
    second: read("second"),
  };
}

/** The day `at` falls on at the tournament, in the `YYYY-MM-DD` shape a fixture carries. */
export function tournamentDay(at: Date): string {
  const clock = tournamentClockParts(at);
  return `${String(clock.year)}-${pad(clock.month)}-${pad(clock.day)}`;
}

/** `09:12` — what a wall clock in the host city read at `at`, 24-hour. */
export function tournamentTimeOfDay(at: Date): string {
  const clock = tournamentClockParts(at);
  return `${pad(clock.hour)}:${pad(clock.minute)}`;
}

/** `2026-10-04 09:12` — the day and the time, for a surface where both are the useful fact. */
export function tournamentDayAndClock(at: Date): string {
  return `${tournamentDay(at)} ${tournamentTimeOfDay(at)}`;
}

/** `2026-10-04-0912` — the same moment as a file name will take it: no colon, no space. */
export function tournamentFileStamp(at: Date): string {
  const clock = tournamentClockParts(at);
  return `${tournamentDay(at)}-${pad(clock.hour)}${pad(clock.minute)}`;
}

/** `04 Oct` — a date a reader takes in at a glance, on the tournament's calendar. */
export function tournamentDateShort(at: Date): string {
  return DATE_SHORT.format(at);
}

/** `04 Oct 2026` — the same, where the year is part of the fact being recorded. */
export function tournamentDateLong(at: Date): string {
  return DATE_LONG.format(at);
}

/**
 * A stored instant, split into the day it falls on at the tournament and the time shown there.
 *
 * RUN 82H's original: a hearing's `hearingAt` reaches the page as an instant in UTC
 * (`2026-09-24T05:00:00.000Z` for a hearing at 14:00 in Tokyo). The desk used to cut the day
 * and the time straight out of that string, so every hearing alert printed the UTC time — nine
 * hours early — and a hearing before 09:00 in Tokyo was drawn on the day before. `null` for a
 * string that is not an instant, because a guessed date is worse than no date.
 */
export function tournamentDayAndTime(instant: string): { day: string; time: string } | null {
  const at = new Date(instant);
  if (Number.isNaN(at.getTime())) return null;
  return { day: tournamentDay(at), time: tournamentTimeOfDay(at) };
}

/**
 * The UTC milliseconds a set of clock parts would name if they were read in UTC.
 *
 * Built with the setters rather than `Date.UTC`, which maps years 0–99 onto 1900–1999 and would
 * turn a two-digit year into a silently wrong instant.
 */
function utcMsOf(parts: TournamentClockParts): number {
  const at = new Date(0);
  at.setUTCFullYear(parts.year, parts.month - 1, parts.day);
  at.setUTCHours(parts.hour, parts.minute, parts.second, 0);
  return at.getTime();
}

/** How far ahead of UTC the tournament's clock is running at `at`, in milliseconds. */
function offsetMsAt(at: Date): number {
  // The parts carry no milliseconds, so the instant they are compared against must not either,
  // or every offset would come back short by however many milliseconds `at` happened to hold.
  const whole = Math.floor(at.getTime() / 1000) * 1000;
  return utcMsOf(tournamentClockParts(at)) - whole;
}

/**
 * **The inverse.** A date and a time as a wall clock in the host city shows them, back into the
 * instant they name — `2026-09-23` + `20:00` → `2026-09-23T11:00:00.000Z`.
 *
 * This is the half that a device-zone bug hides in, because `new Date("2026-09-23T20:00")` — an
 * offset-less local date-time — is read in the DEVICE's zone and looks perfectly correct on a
 * laptop that is already set to Tokyo. On a laptop set to Los Angeles the same two boxes record
 * a moment sixteen hours later, and the case file, which reads `hearingAt` back in Tokyo, then
 * shows the TD a hearing he did not enter.
 *
 * **Two passes**, because the offset has to be looked up at an instant and the instant is what
 * is being solved for. The first pass reads the zone's offset near the wall time and subtracts
 * it; the second re-reads it at the instant that produced, which is what matters in a zone that
 * changes offset — Asia/Tokyo has kept +09:00 without a shift since 1951, so the second pass is
 * belt and braces here, but this function is named for the tournament and not for Japan, and a
 * tournament in a DST zone would need it.
 *
 * **What it does at a DST seam, stated accurately** (RUN 83 QA — an earlier draft of this
 * paragraph claimed the wrong behaviour, and neither path is exercised, because the zone is a
 * module constant with no injection point). Across a spring-forward gap, where the wall time
 * names no instant at all, it does NOT settle on the moment the clock jumped to: a 02:30 typed
 * in a zone that skipped from 02:00 to 03:00 resolves to an instant that reads back as 01:30,
 * an hour BEFORE the gap. Across a fall-back repeat, where the wall time names two instants, it
 * always takes the first. Both are defensible for a form that must record something, and both
 * are moot for Asia/Tokyo, which has not shifted since 1951. If this module is ever reused for a
 * tournament in a DST zone, take the zone as a parameter first so the seam can be tested.
 *
 * `null` for a date or a time that is not one — this parses, it does not guess.
 */
export function instantFromTournamentTime(date: string, time: string): Date | null {
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  // `<input type="time">` yields `HH:MM`, and `HH:MM:SS` when a `step` asks for seconds.
  const clock = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(time);
  if (day === null || clock === null) return null;

  const wanted: TournamentClockParts = {
    year: Number(day[1]),
    month: Number(day[2]),
    day: Number(day[3]),
    hour: Number(clock[1]),
    minute: Number(clock[2]),
    second: Number(clock[3] ?? "0"),
  };
  if (wanted.month < 1 || wanted.month > 12 || wanted.day < 1 || wanted.day > 31) return null;
  if (wanted.hour > 23 || wanted.minute > 59 || wanted.second > 59) return null;

  const wall = utcMsOf(wanted);
  const first = new Date(wall - offsetMsAt(new Date(wall)));
  const settled = new Date(wall - offsetMsAt(first));
  if (Number.isNaN(settled.getTime())) return null;

  // A day the calendar does not have — 31 April, 30 February — rolls forward in `Date` and
  // would be recorded as a day nobody typed. The reading has to name the parts it was given.
  const back = tournamentClockParts(settled);
  if (back.year !== wanted.year || back.month !== wanted.month || back.day !== wanted.day) {
    return null;
  }
  return settled;
}
